/**
 * Saved Accounts Management Utility
 * Handles saving, retrieving, and removing user login credentials in localStorage
 */

const SAVED_ACCOUNTS_KEY = 'kayease_saved_accounts';

/**
 * Get all saved accounts from localStorage
 * @returns {Array} Array of saved account objects
 */
export const getSavedAccounts = () => {
    if (typeof window === 'undefined') return [];

    try {
        const saved = localStorage.getItem(SAVED_ACCOUNTS_KEY);
        return saved ? JSON.parse(saved) : [];
    } catch (error) {
        console.error('Error reading saved accounts:', error);
        return [];
    }
};

/**
 * Save a new account or update existing one
 * @param {Object} account - Account object with schoolCode, phoneNumber, and optional userName
 */
export const saveAccount = (account) => {
    if (typeof window === 'undefined') return;

    try {
        const { schoolCode, phoneNumber, userName = null, userRole = null, photoUrl = null, associatedCount = 1 } = account;

        if (!schoolCode || !phoneNumber) {
            console.error('School code and phone number are required');
            return;
        }

        const savedAccounts = getSavedAccounts();

        // Check if account already exists
        const existingIndex = savedAccounts.findIndex(
            acc => acc.schoolCode === schoolCode && acc.phoneNumber === phoneNumber
        );

        const accountData = {
            schoolCode,
            phoneNumber,
            userName,
            userRole,
            photoUrl,
            associatedCount,
            savedAt: new Date().toISOString(),
            lastUsed: new Date().toISOString(),
        };

        if (existingIndex >= 0) {
            // Update existing account
            savedAccounts[existingIndex] = {
                ...savedAccounts[existingIndex],
                ...accountData,
            };
        } else {
            // Add new account
            savedAccounts.push(accountData);
        }

        // Keep only the last 5 accounts
        const limitedAccounts = savedAccounts.slice(-5);

        localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(limitedAccounts));
    } catch (error) {
        console.error('Error saving account:', error);
    }
};

/**
 * Remove a saved account
 * @param {string} schoolCode - School code of the account to remove
 * @param {string} phoneNumber - Phone number of the account to remove
 */
export const removeSavedAccount = (schoolCode, phoneNumber) => {
    if (typeof window === 'undefined') return;

    try {
        const savedAccounts = getSavedAccounts();
        const filteredAccounts = savedAccounts.filter(
            acc => !(acc.schoolCode === schoolCode && acc.phoneNumber === phoneNumber)
        );

        localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(filteredAccounts));
    } catch (error) {
        console.error('Error removing saved account:', error);
    }
};

/**
 * Update last used timestamp for an account
 * @param {string} schoolCode - School code
 * @param {string} phoneNumber - Phone number
 */
export const updateLastUsed = (schoolCode, phoneNumber) => {
    if (typeof window === 'undefined') return;

    try {
        const savedAccounts = getSavedAccounts();
        const accountIndex = savedAccounts.findIndex(
            acc => acc.schoolCode === schoolCode && acc.phoneNumber === phoneNumber
        );

        if (accountIndex >= 0) {
            savedAccounts[accountIndex].lastUsed = new Date().toISOString();
            localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(savedAccounts));
        }
    } catch (error) {
        console.error('Error updating last used:', error);
    }
};

/**
 * Clear all saved accounts
 */
export const clearAllSavedAccounts = () => {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(SAVED_ACCOUNTS_KEY);
    } catch (error) {
        console.error('Error clearing saved accounts:', error);
    }
};

/**
 * Get initials from name for avatar
 * @param {string} name - User name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
